import React, { useEffect, useContext, useState, useRef } from "react";
import {
  Skeleton,
  TableHead,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import MUICard from "../../../Component/UI/MUICard/MUICard";
import MUITable, {
  StyledTableCell,
  StyledTableRow,
} from "../../../Component/UI/Table/Table";
import { DiscoveryContext } from "../discovery-context";
import { extractDomain } from "../../../utils/helpers";
import classes from "./Taxonomy.module.css";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import MUIToolTip from "../../../Component/UI/ActionTooltip/MUIToolTip";
import api from "../../../BrandOnBoarding/api";
import useGenerateProductTaxonomies from "../useGenerateProductTaxonomies";
import { formatSourceData, formatOldSourceData } from "../../../utils/metric-sources"
import BrandAvatar from "../../../Component/Avatar/Avatar"
import { ENTITY_LABELS } from "../../../utils/labelMapping";

const TABLE_HEADERS = [
  {
    id: "taxonomy",
    name: `${ENTITY_LABELS.Product} Dimensions`,
  },
];

function Row({ data }) {
  const [open, setOpen] = useState(false);
  const discoveryCtx = useContext(DiscoveryContext);
  const showOldSourcesRecord = discoveryCtx.state.sourceValidationStatus === "PENDING";
  const isValidated = discoveryCtx?.taxonomies?.isValidated || false;
  const sourceTaxonomy = discoveryCtx?.taxonomies?.sources || [];
  function roundNumber(num) {
    return num % 1 < 0.5 ? Math.floor(num) : Math.ceil(num);
  }
  const llm = discoveryCtx.llm;
  const category = discoveryCtx.category;
  const market = discoveryCtx.market;

  return (
    <React.Fragment key={data.name}>
      <StyledTableRow>
        <StyledTableCell className={classes.attribute_headings}>
          <div className={classes.heading_wrapper}>
            <MUIIconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </MUIIconButton>
             <span className={classes.heading_text}>
              {data.name.replace(/\bProduct\b/g, ENTITY_LABELS.Product)}
            </span>
          </div>
        </StyledTableCell>
        {data?.products?.map((product) => {
          let color = "#dc3545";
          let rating = "low";
          if (product?.score >= 7) {
            rating = "fair";
            color = "#2196F3";
          }
          if (product?.score >= 9) {
            rating = "high";
            color = "#198754";
          }


          const attributeSources = Array.isArray(product?.sources) && product?.sources?.length > 0 ? product?.sources : sourceTaxonomy;
          const isCustomTooltip =
            product?.name?.toLowerCase() === discoveryCtx.entity?.toLowerCase() &&
            llm?.toLowerCase()?.includes("search") &&
            (showOldSourcesRecord || isValidated);

           const sources = isCustomTooltip
                      ? (showOldSourcesRecord
                        ? formatOldSourceData(attributeSources, llm)
                        : formatSourceData(attributeSources, llm))
                      : {};
          const TooltipComponent = isCustomTooltip ? MUIToolTip : Tooltip;


          const tooltipProps = isCustomTooltip
            ? {
               metric: `${ENTITY_LABELS.Product} Dimensions`,
              attribute: data.name,
              metricScore: Number(product?.score).toFixed(0),
              selectedInput: {
                llm,
                market,
                category,
                brand: product.name,
                product: product.name,
              },
              addToActionHandler: () => { },
              actionText: "Add To Action",
              isActionLoading: false,
              title: product?.explanation,
              arrow: true,
              isDiscovery: true,
              sources,
              isBrandAudit: true,
              isBrandOverview: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              }
            }
            : {
              title: product?.explanation,
              arrow: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              },
            };

          return (
            <>
              <TooltipComponent {...tooltipProps}>
                <StyledTableCell
                  style={{ color }}
                  className={classes.attribute_score}
                  key={product.name}
                >
                  {roundNumber(product?.score) || 0}
                </StyledTableCell>
              </TooltipComponent>
            </>
          );
        })}
      </StyledTableRow>
      {open &&
        data?.products?.[0]?.subcategories?.map((sub, idx) => (
          <StyledTableRow key={idx} className={classes.sub_row}>
            <StyledTableCell className={classes.attribute_sub_headings}>
              {sub.name}
            </StyledTableCell>
            {data?.products?.map((product, jdx) => {
              let color = "#dc3545";
              const item = product?.subcategories?.[idx];
              if (item?.score >= 7) {
                color = "#2196F3";
              }
              if (item?.score >= 9) {
                color = "#198754";
              }

              return (
                <Tooltip
                  title={item?.explanation}
                  arrow
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -18],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <StyledTableCell
                    style={{ color }}
                    className={classes.attribute_score}
                  >
                    {product?.subcategories?.[idx]?.score || 0}
                  </StyledTableCell>
                </Tooltip>
              );
            })}
          </StyledTableRow>
        ))}
    </React.Fragment>
  );
}

export default function Taxonomy() {
  // const [error, setError] = useState(null);
  const discoveryCtx = useContext(DiscoveryContext);
  const { custom_taxonomies } = useGenerateProductTaxonomies();
  const abortControllerRef = useRef(null);


  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(10)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              variant="rectangular"
              width={index % 2 === 0 ? "70%" : "97%"}
              key={index}
              className="my-3"
              height={20}
            />
          ))}
      </div>
    );
  }


  function renderHeaders() {
    const headers = [
      ...TABLE_HEADERS,
      ...discoveryCtx.competitors.map((item) => ({
        id: item.name,
        name: item.name,
      })),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let ctxCompetitor = null;
            if (index !== 0) {
              ctxCompetitor = discoveryCtx.competitors[index - 1];
            }

            let domain = "";
            if (ctxCompetitor && ctxCompetitor?.domain) {
              domain = extractDomain(ctxCompetitor?.domain);
            }

            return (
              <TableCell key={index} className={classes.table_head_cell}>
                <Tooltip title={header.name}>
                  <div className={classes.table_head_content}>
                    {index != 0 && (
                      // <img
                      //   data-html2canvas-ignore
                      //   className={classes.table_head_logo}
                      //   src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                      // />
                      <BrandAvatar
                        domainName={domain}
                        brandName={header.name}
                      />
                    )}
                    <div
                      className={
                        index === 0
                          ? classes.table_head_text_comp
                          : classes.table_head_text
                      }
                    >
                      {header.name}
                    </div>
                  </div>
                </Tooltip>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  function roundNumber(num) {
    return num % 1 < 0.5 ? Math.floor(num) : Math.ceil(num);
  }

  function renderRows(category) {
    return <Row key={category.name} data={category} />;
  }

  let loading = true;
  let hasError = false;
  let categories = [];
  const transactionStatus = discoveryCtx.state.transactionStatus

  if (
    discoveryCtx?.taxonomies.data &&
    Object.keys(discoveryCtx?.taxonomies.data || {}).length > 0
  ) {
    categories = discoveryCtx?.taxonomies?.data?.categories.map((category) => ({
      name: category.name,
      products: discoveryCtx?.taxonomies?.data.products.map((product) => {
        const categoryScores = product?.scores?.[category.name];
        const subcategories = Object.values(
          product.scores[category.name]?.subcategories || {}
        );
        const totalScore = subcategories.length
          ? subcategories.reduce(
            (acc, item) => acc + (isNaN(+item.score) ? 0 : +item.score),
            0
          )
          : 0;
        const average = subcategories.length
          ? totalScore / subcategories.length
          : 0;

        //  Merge all subcategory sources (no subcategory field)
        const categoriesSources = Object.keys(categoryScores?.subcategories || {}).reduce(
          (acc, subcat) => {
            const subSources = categoryScores?.subcategories?.[subcat]?.sources || [];
            return [...acc, ...subSources];
          },
          []
        );

        return {
          name: product.name,
          score: roundNumber(average),
          sources: categoriesSources,
          subcategories:
            Object.keys(
              product.scores[category.name]?.subcategories || {}
            )?.map((subcat) => ({
              name: subcat,
              sources:
                product.scores[category.name]?.subcategories?.[subcat].sources,
              score:
                product.scores[category.name]?.subcategories?.[subcat].score,
              explanation:
                product.scores[category.name]?.subcategories?.[subcat]
                  .explanation,
            })) || [],
        };
      }),
    }));

    const headerOrder = discoveryCtx.competitors.map((h) => h.name);
    categories = categories.map((category) => ({
      ...category,
      products: category.products.sort(
        (a, b) => headerOrder.indexOf(a.name) - headerOrder.indexOf(b.name)
      ),
    }));

    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }
console.log("categoriesRecords",categories)
  return (
    <MUICard variant="elevated" elevation={0} className={classes.card}>
      {!loading && !hasError && categories?.length > 0 && (
        <MUITable
          elevation={0}
          data={categories}
          renderHeaders={() => renderHeaders()}
          render={renderRows}
        />
      )}
      {hasError && (
        <div className={classes.error}>
          <ErrorView
            title={"Error Fetching Product Dimensions"}
            message={"Request Failed"}
          />
        </div>
      )}
      {loading && !hasError && <LoadingSkeletion />}
    </MUICard>
  );
}
